import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, Search, Grid, List, Eye, EyeOff, User, UserCheck, UserX, Shield, ShieldCheck, Crown, Filter, X, Calendar, Mail, Phone } from 'lucide-react';

const UsersManagement = () => {
  const { user: currentUser, restaurants: userRestaurants, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    isActive: '',
    role: '',
    hasLoggedIn: ''
  });
  const [formData, setFormData] = useState({
    restaurantId: '',
    username: '',
    email: '',
    passwordHash: '',
    firstName: '',
    lastName: '',
    role: 'Admin',
    isActive: true
  });

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // API Services
  const userService = {
    getAll: async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Users API error:', error);
        throw error;
      }
    },
    create: async (data) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },
    update: async (id, data) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // NoContent (204) durumunda response.json() çağırma
      if (response.status === 204) {
        return { success: true };
      }
      
      // Diğer durumlarda JSON parse etmeye çalış
      try {
        return await response.json();
      } catch (error) {
        return { success: true };
      }
    },
    delete: async (id) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        mode: 'cors'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.ok;
    }
  };

  const restaurantService = {
    getAll: async () => {
      if (!currentUser) return [];
      
      try {
        const token = localStorage.getItem('authToken');
        let url = `${API_BASE_URL}/api/Restaurants`;
        
        // Convert numeric role to string for comparison
        const userRole = typeof currentUser.role === 'number' ? 
          (currentUser.role === 1 ? 'Admin' : currentUser.role === 2 ? 'Manager' : currentUser.role === 3 ? 'SuperAdmin' : 'User') : 
          currentUser.role;
        
        // If user is not SuperAdmin, filter by their restaurant
        if (userRole !== 'SuperAdmin') {
          url = `${API_BASE_URL}/api/Restaurants/user/${currentUser.id}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          return data;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Restaurant API Error:', error);
        throw error;
      }
    }
  };

  // JWT token'dan RestaurantId'yi Guid olarak al
  const getRestaurantIdFromToken = () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('No auth token found');
        return null;
      }

      // Token formatını kontrol et
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('Invalid token format - expected 3 parts');
        return null;
      }

      // Base64 decode işlemini güvenli hale getir
      let payload;
      try {
        // Base64 padding ekle
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
        const decoded = atob(padded);
        payload = JSON.parse(decoded);
      } catch (decodeError) {
        console.error('Failed to decode token payload:', decodeError);
        return null;
      }

      const restaurantIdClaim = payload.RestaurantId;
      
      if (restaurantIdClaim) {
        console.log('RestaurantId from token:', restaurantIdClaim);
        return restaurantIdClaim; // Guid'i direkt döndür
      }
      
      console.log('No RestaurantId found in token payload');
      return null;
    } catch (error) {
      console.error('Error parsing RestaurantId from token:', error);
      return null;
    }
  };

  // Load Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersData, restaurantsData] = await Promise.all([
                userService.getAll(),
                restaurantService.getAll()
            ]);

            setUsers(usersData);
            setRestaurants(restaurantsData);
        } catch (error) {
            console.error('❌ Error fetching data:', error);
            setUsers([]);
            setRestaurants([]);
            alert(`API Hatası: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

  const createUser = async () => {
    try {
      // Validation
      if (!formData.restaurantId) {
        alert('Lütfen bir restoran seçin');
        return;
      }
      
      // Role'ü string'den int'e çevir
      const roleMap = {
        'Admin': 1,
        'Manager': 2,
        'SuperAdmin': 3
      };
      
      // Form'dan gelen restaurantId'yi kullan
      const requestData = {
        restaurantId: formData.restaurantId,
        username: formData.username,
        email: formData.email,
        passwordHash: formData.passwordHash,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: roleMap[formData.role] || 1, // String role'ü int'e çevir
        isActive: formData.isActive
      };
      
      await userService.create(requestData);
      await fetchData();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Kullanıcı oluşturulurken bir hata oluştu!');
    }
  };

  const updateUser = async () => {
    if (!editingUser) {
      return;
    }
    
    try {
      // Validation
      if (!formData.restaurantId) {
        alert('Lütfen bir restoran seçin');
        return;
      }
      
      // Role'ü string'den int'e çevir
      const roleMap = {
        'Admin': 1,
        'Manager': 2,
        'SuperAdmin': 3
      };
      
      // Form'dan gelen restaurantId'yi kullan
      const requestData = {
        restaurantId: formData.restaurantId,
        username: formData.username,
        email: formData.email,
        passwordHash: formData.passwordHash,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: roleMap[formData.role] || 1, // String role'ü int'e çevir
        isActive: formData.isActive
      };
      
      const updateResult = await userService.update(editingUser.id, requestData);
      
      if (updateResult.success) {
        await fetchData();
        resetForm();
        setShowModal(false);
        setEditingUser(null);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Kullanıcı güncellenirken bir hata oluştu!');
    }
  };

  const deleteUser = async (id) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      try {
        await userService.delete(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Kullanıcı silinirken bir hata oluştu!');
      }
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      const updatedData = { ...user, isActive: !user.isActive };
      await userService.update(user.userId, updatedData);
      await fetchData();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('Kullanıcı durumu değiştirilirken bir hata oluştu!');
    }
  };

  const resetForm = () => {
    setFormData({
      restaurantId: '',
      username: '',
      email: '',
      passwordHash: '',
      firstName: '',
      lastName: '',
      role: 'Admin',
      isActive: true
    });
  };

  const openModal = (user) => {
    if (user) {
      setEditingUser(user);
      const formDataToSet = {
        restaurantId: user.restaurantId || '',
        username: user.username,
        email: user.email,
        passwordHash: '', // Don't prefill password for security
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        isActive: user.isActive
      };
      setFormData(formDataToSet);
    } else {
      setEditingUser(null);
      resetForm();
    }
    setShowModal(true);
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    return restaurant ? restaurant.name : 'Bilinmeyen Restoran';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SuperAdmin': return <Crown className="w-4 h-4" />;
      case 'Admin': return <ShieldCheck className="w-4 h-4" />;
      case 'Manager': return <Shield className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SuperAdmin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Admin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Manager': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFullName = (user) => {
    return `${user.firstName} ${user.lastName}`.trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Hiç giriş yapmamış';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAccountAge = (createdDate) => {
    const now = new Date();
    const created = new Date(createdDate);
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Bugün';
    if (diffDays < 7) return `${diffDays} gün`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} ay`;
    return `${Math.floor(diffDays / 365)} yıl`;
  };

  const clearFilters = () => {
    setFilters({
      isActive: '',
      role: '',
      hasLoggedIn: ''
    });
    setSelectedRestaurant('');
    setSearchTerm('');
  };

  const filteredUsers = users.filter(user => {
    // Text search
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getFullName(user).toLowerCase().includes(searchTerm.toLowerCase());

    // Restaurant filter
    const matchesRestaurant = !selectedRestaurant || user.restaurantId === selectedRestaurant;

    // Advanced filters
    const matchesActive = filters.isActive === '' || 
      (filters.isActive === 'true' && user.isActive) ||
      (filters.isActive === 'false' && !user.isActive);

    const matchesRole = !filters.role || user.role === filters.role;

    const matchesLoggedIn = filters.hasLoggedIn === '' ||
      (filters.hasLoggedIn === 'true' && user.lastLoginDate) ||
      (filters.hasLoggedIn === 'false' && !user.lastLoginDate);

    return matchesSearch && matchesRestaurant && matchesActive && matchesRole && matchesLoggedIn;
  });

  const userRoles = ['SuperAdmin', 'Admin', 'Manager'];

  useEffect(() => {
    fetchData();
    
    // Eğer sadece 1 restaurant varsa, onu otomatik seç (filter için)
    if (userRestaurants && userRestaurants.length === 1) {
      const restaurantId = userRestaurants[0].id || userRestaurants[0].restaurantId;
      setSelectedRestaurant(restaurantId);
    }
  }, [userRestaurants]);

  // RestaurantId'yi token'dan al ve formData'yı güncelle
  useEffect(() => {
    const restaurantId = getRestaurantIdFromToken();
    if (restaurantId) {
      setFormData(prev => ({
        ...prev,
        restaurantId: restaurantId
      }));
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Kullanıcı Yönetimi</h1>
              <p className="text-gray-600">
                {currentUser.role === 'SuperAdmin' 
                  ? 'Tüm sistem kullanıcılarını yönetin' 
                  : 'Kendi restoranınızın kullanıcılarını yönetin'
                }
              </p>
            </div>
            
            {/* User Info */}
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                {currentUser.role === 'SuperAdmin' ? <Crown className="w-4 h-4 text-purple-600" /> : 
                 currentUser.role === 'Admin' ? <Shield className="w-4 h-4 text-blue-600" /> : 
                 <User className="w-4 h-4 text-green-600" />}
                {currentUser.role}
              </span>
              <span>•</span>
              <span>{restaurants.length} restoran</span>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Kullanıcı ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Restaurant Filter */}
              <select
                value={selectedRestaurant}
                onChange={(e) => setSelectedRestaurant(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tüm Restoranlar</option>
                {restaurants.map(restaurant => (
                  <option key={restaurant.id} value={restaurant.id}>
                    {restaurant.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              {/* Advanced Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 border border-gray-300 rounded-lg ${showFilters ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                title="Advanced Filters"
              >
                <Filter className="w-4 h-4" />
              </button>

              {/* Clear Filters */}
              <button
                onClick={clearFilters}
                className="p-2 border border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
                title="Clear Filters"
              >
                <X className="w-4 h-4" />
              </button>

              {/* View Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* Add Button */}
              {hasPermission('manage_users') && (
                <button
                  onClick={() => openModal()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Yeni Kullanıcı
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
                  <select
                    value={filters.isActive}
                    onChange={(e) => setFilters({...filters, isActive: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters({...filters, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tüm Roller</option>
                    {userRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                {/* Login Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Giriş Durumu</label>
                  <select
                    value={filters.hasLoggedIn}
                    onChange={(e) => setFilters({...filters, hasLoggedIn: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    <option value="true">Giriş Yapmış</option>
                    <option value="false">Hiç Giriş Yapmamış</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{users.length}</div>
                  <div className="text-sm text-gray-500">Toplam Kullanıcı</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.isActive).length}
                  </div>
                  <div className="text-sm text-gray-500">Aktif Kullanıcı</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Crown className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'SuperAdmin').length}
                  </div>
                  <div className="text-sm text-gray-500">Super Admin</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.lastLoginDate).length}
                  </div>
                  <div className="text-sm text-gray-500">Giriş Yapmış</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Grid/List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Kullanıcı bulunamadı</h3>
            <p className="text-gray-500 mb-4">Arama kriterlerinize uygun kullanıcı bulunmuyor.</p>
            <button
              onClick={() => openModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              İlk kullanıcıyı oluştur
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <div key={user.userId} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{getFullName(user)}</h3>
                        <p className="text-sm text-gray-500">@{user.username}</p>
                        <p className="text-xs text-gray-400">{getRestaurantName(user.restaurantId)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getRoleColor(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role}
                      </span>
                      <button
                        onClick={() => toggleUserStatus(user)}
                        className={`p-1.5 rounded-full ${user.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                        title={user.isActive ? 'Aktif' : 'Pasif'}
                      >
                        {user.isActive ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  </div>

                  {/* Account Info */}
                  <div className="space-y-2 mb-4 text-xs text-gray-500">
                    <div className="flex items-center justify-between">
                      <span>Hesap Yaşı:</span>
                      <span>{getAccountAge(user.createdDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Son Giriş:</span>
                      <span>{user.lastLoginDate ? formatDate(user.lastLoginDate) : 'Hiç'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openModal(user)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Düzenle"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteUser(user.userId)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
                <div className="col-span-3">Kullanıcı</div>
                <div className="col-span-2">Restoran</div>
                <div className="col-span-2">E-posta</div>
                <div className="col-span-1">Rol</div>
                <div className="col-span-2">Son Giriş</div>
                <div className="col-span-1">Durum</div>
                <div className="col-span-1">İşlemler</div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <div key={user.userId} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{getFullName(user)}</div>
                          <div className="text-xs text-gray-500">@{user.username}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600">{getRestaurantName(user.restaurantId)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600 truncate">{user.email}</span>
                    </div>
                    <div className="col-span-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getRoleColor(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(user.lastLoginDate)}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openModal(user)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Düzenle"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteUser(user.userId)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Restaurant Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Restoran *
                  </label>
                  <select
                  value={formData.restaurantId}
                    onChange={(e) => setFormData({...formData, restaurantId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Restoran seçin</option>
                    {restaurants.map((restaurant) => (
                      <option key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ad *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Soyad *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Account Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kullanıcı Adı *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {editingUser ? 'Yeni Şifre (Boş bırakılırsa değişmez)' : 'Şifre *'}
                  </label>
                  <input
                    type="password"
                    value={formData.passwordHash}
                    onChange={(e) => setFormData({...formData, passwordHash: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={!editingUser}
                    minLength="6"
                  />
                  {!editingUser && (
                    <p className="mt-1 text-sm text-gray-500">Minimum 6 karakter olmalıdır</p>
                  )}
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {userRoles.map(role => (
                      <label key={role} className="relative">
                        <input
                          type="radio"
                          name="role"
                          value={role}
                          checked={formData.role === role}
                          onChange={(e) => setFormData({...formData, role: e.target.value})}
                          className="sr-only"
                        />
                        <div className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.role === role 
                            ? getRoleColor(role).replace('bg-', 'bg-').replace('100', '50') + ' border-current'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="flex items-center gap-2">
                            {getRoleIcon(role)}
                            <span className="font-medium">{role}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {role === 'SuperAdmin' && 'Tüm yetkilere sahip'}
                            {role === 'Admin' && 'Restoran yöneticisi'}
                            {role === 'Manager' && 'Sınırlı yetkilere sahip'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                {formData.firstName && formData.lastName && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Kullanıcı Önizlemesi</h4>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {formData.firstName.charAt(0)}{formData.lastName.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-blue-900">
                          {formData.firstName} {formData.lastName}
                        </div>
                        <div className="text-xs text-blue-700">@{formData.username}</div>
                        {formData.email && (
                          <div className="text-xs text-blue-600">{formData.email}</div>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getRoleColor(formData.role)}`}>
                        {getRoleIcon(formData.role)}
                        {formData.role}
                      </span>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Aktif Kullanıcı</span>
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    Pasif kullanıcılar sisteme giriş yapamaz
                  </p>
                </div>

                {/* Role Permissions Info */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">🔐 Rol Yetkileri</h4>
                  <div className="text-sm text-yellow-800 space-y-2">
                    <div>
                      <strong className="flex items-center gap-1">
                        <Crown className="w-4 h-4" />
                        SuperAdmin:
                      </strong>
                      <span className="ml-1">Tüm restoranları yönetebilir, kullanıcı oluşturabilir</span>
                    </div>
                    <div>
                      <strong className="flex items-center gap-1">
                        <ShieldCheck className="w-4 h-4" />
                        Admin:
                      </strong>
                      <span className="ml-1">Kendi restoranını tam olarak yönetebilir</span>
                    </div>
                    <div>
                      <strong className="flex items-center gap-1">
                        <Shield className="w-4 h-4" />
                        Manager:
                      </strong>
                      <span className="ml-1">Ürün ve kategori yönetimi, sınırlı yetkiler</span>
                    </div>
                  </div>
                </div>

                {/* Security Warning */}
                {editingUser && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <div className="text-orange-600 mt-0.5">⚠️</div>
                      <div className="text-sm text-orange-800">
                        <strong>Güvenlik Uyarısı:</strong> Kullanıcı bilgilerini değiştirdiğinizde, 
                        kullanıcının bir sonraki girişinde yeni bilgiler geçerli olacaktır. 
                        Şifre değişikliği yaparsanız kullanıcıyı bilgilendirmeyi unutmayın.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingUser(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={editingUser ? updateUser : createUser}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                {editingUser ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;